/*
 * GWA: Picks a lighter MP4 rendition for videos rendered by snippets/gwa-video.liquid.
 *
 * Shopify's `video.sources` only exposes the 1080p MP4, while the HLS playlist also lists 720p and 480p renditions,
 * which are available as MP4 on the CDN. Browsers with native HLS support (Safari / iOS) use the adaptive HLS source
 * directly. For all other browsers, this script reads the playlist before the video comes into view and swaps in the
 * 480p (mobile) or 720p (desktop) MP4. If anything fails, the original source is kept / restored.
 */
(() => {
  if (window.gwaLazyVideo) return;
  window.gwaLazyVideo = true;

  if (document.createElement('video').canPlayType('application/vnd.apple.mpegurl')) return;

  const RENDITION_PATTERN = /^(.*)\.((?:HD|SD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/;

  // WeakSets instead of data attributes, so cloned slides (e.g. infinite sliders) are still processed
  const swapped = new WeakSet();
  const observed = new WeakSet();

  const maxHeight = () => (window.matchMedia('(max-width: 749px)').matches ? 480 : 720);

  async function swapSource(video) {
    if (swapped.has(video)) return;
    swapped.add(video);

    const hlsSource = video.querySelector('source[type="application/x-mpegURL"], source[type="application/vnd.apple.mpegurl"]');
    const mp4Source = video.querySelector('source[type="video/mp4"]');
    if (!hlsSource || !mp4Source) return;

    try {
      const playlistUrl = hlsSource.src;
      const playlist = await (await fetch(playlistUrl)).text();
      const lines = playlist.split('\n');
      const renditions = [];

      lines.forEach((line, index) => {
        const resolution = line.match(/RESOLUTION=(\d+)x(\d+)/);
        const uri = (lines[index + 1] || '').trim();
        const match = uri.match(RENDITION_PATTERN);

        if (resolution && match) {
          renditions.push({
            height: Number(resolution[2]),
            url: new URL(`${match[1]}.${match[2]}-${match[3]}-${match[4]}.mp4`, playlistUrl).href
          });
        }
      });

      const limit = maxHeight();
      const candidates = renditions.filter((rendition) => rendition.height <= limit).sort((a, b) => b.height - a.height);
      if (!candidates.length) return;

      const originalUrl = mp4Source.src;
      const wasPlaying = !video.paused;

      video.addEventListener('error', () => {
        if (video.src === originalUrl) return;
        video.src = originalUrl;
        video.play().catch(() => {});
      }, { once: true });

      hlsSource.remove();
      mp4Source.src = candidates[0].url;
      video.load();

      if (wasPlaying) {
        video.play().catch(() => {});
      }
    } catch (error) {
      // Keep the original source
    }
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      swapSource(entry.target);
    });
  }, { rootMargin: '800px 0px' });

  const scan = () => {
    document.querySelectorAll('video[data-gwa-lazy-video]').forEach((video) => {
      if (observed.has(video)) return;
      observed.add(video);
      observer.observe(video);
    });
  };

  scan();
  document.addEventListener('DOMContentLoaded', scan);
  window.addEventListener('load', scan);
  document.addEventListener('shopify:section:load', scan);
})();
